// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Center, Checkbox, Divider, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { FC, useContext } from 'react';
import { IconChevronDown } from '@tabler/icons';
import { FormatS } from '../../Service/FormatS';
import { DataS } from '../../Service/DataS';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { TestS } from '../../TestS';
import BookitupSegmentedControl from '../../Atoms/BookitupSegmentedControl';
import { BaseDocument, DiscountType } from '../../Service/DocumentT';
import { Currency } from '../../Types/BookitupDocument';
import DocumentLanguageS from '../../Service/DocumentLanguageS';

interface Props {
  baseDocument: BaseDocument;
  savePatch: (patch: Partial<BaseDocument>) => void;
}

const DocumentSum: FC<Props> = ({ baseDocument, savePatch }) => {
  const { profile } = useContext(ProfileContext);
  const {
    discount,
    summable,
    documentType,
    taxSummary,
    taxType,
    finalNet,
    positions,
    subtotal,
    sum,
    currency,
    currencySymbol,
    language,
  } = baseDocument;
  const t = (id: string) => DocumentLanguageS.translate(language, id);
  const {
    value: discountValue,
    type: discountType,
    description: discountDescription,
    amount: discountAmount,
  } = discount;
  const { smallScaleBusiness } = profile ?? {};
  const negativeSum = subtotal - discountAmount < 0;

  if (positions.length === 0) {
    return null;
  }
  return (
    <Group position="right">
      <Stack
        sx={(theme) => ({ maxWidth: 375, width: '100%', color: theme.colorScheme === 'dark' ? 'white' : 'black' })}
      >
        {summable ? (
          <Stack
            spacing={0}
            sx={(theme) => ({
              border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
              borderRadius: 10,
            })}
          >
            {documentType !== 'PARTIAL_INVOICE' && (
              <>
                <div>
                  <Group position="apart" m="sm">
                    <Text>
                      {t('document.sum.subtotal')} {taxType === 'net' ? t('document.position.net') : ''}
                    </Text>
                    <Text> {FormatS.formatPriceWithCurrency(currency, subtotal)} </Text>
                  </Group>
                  <Divider />
                </div>
                <Group position="apart" m="sm">
                  <Group spacing={0}>
                    <Text mr="xs">{t('document.position.discount')}</Text>
                    <NumberInput
                      value={discountValue}
                      hideControls
                      decimalSeparator=","
                      precision={2}
                      sx={{ width: 65 }}
                      onBlur={(e) => {
                        const intVal = parseFloat(e.target.value.replace(',', '.'));
                        const finalVal = isNaN(intVal) || intVal < 0 ? 0 : intVal;
                        savePatch({ discountValue: finalVal });
                      }}
                      min={0}
                      data-testid={TestS.Documents.Edit.DISCOUNT_VALUE}
                    />
                    <BookitupSegmentedControl
                      value={discountType}
                      data={DataS.getDiscountUnitOptions(currencySymbol)}
                      onChange={(val: DiscountType) => val && savePatch({ discountType: val })}
                      testId={TestS.Documents.Edit.DISCOUNT_TYPE}
                    />
                  </Group>
                  <Text> {`- ${FormatS.formatPriceWithCurrency(currency, discountAmount)}`}</Text>
                </Group>
                <TextInput
                  ml="sm"
                  mr="sm"
                  mb="sm"
                  label={t('document.sum.discount_note')}
                  defaultValue={discountDescription}
                  placeholder={t('document.sum.discount_placeholder')}
                  onBlur={(e) => savePatch({ discountDescription: e.target.value })}
                />
                <Divider />
              </>
            )}
            {taxType === 'net' && !smallScaleBusiness && taxSummary && (
              <>
                <Stack p="xs">
                  <Group position="apart">
                    <Text>{t('document.sum.total_net')}</Text>
                    <Text>{FormatS.formatPriceWithCurrency(currency, finalNet)}</Text>
                  </Group>
                  {taxSummary.taxEntries.map((taxEntry) =>
                    taxEntry.taxRatePercent === 0 ? null : (
                      <Group position="apart">
                        <Text key={taxEntry.taxRatePercent} data-testid={TestS.Documents.Edit.TAX}>
                          {`${t('document.partial_invoice.vat')} ${taxEntry.taxRatePercent}%`}
                        </Text>
                        <Text>{FormatS.formatPriceWithCurrency(currency, taxEntry.taxAmount)}</Text>
                      </Group>
                    ),
                  )}
                </Stack>
                <Divider />
              </>
            )}
            <Group position="apart" m="sm">
              <Text>{t('document.sum.currency')}</Text>
              <Select
                value={currency}
                data={['EUR', 'CHF']}
                sx={{ maxWidth: 70 }}
                styles={{ rightSection: { pointerEvents: 'none' } }}
                rightSection={<IconChevronDown size={14} />}
                rightSectionWidth={25}
                onChange={(val: Currency) => savePatch({ currency: val })}
              />
            </Group>
            <Divider />
            <Box
              p="xs"
              sx={(theme) => {
                const [defaultBackground] = theme.colors.gray;
                const [, , , , , darkBackground] = theme.colors.dark;
                let backgroundColor = defaultBackground;
                if (negativeSum) {
                  backgroundColor = '#FFEFEF';
                } else if (theme.colorScheme === 'dark') {
                  backgroundColor = darkBackground;
                }
                return {
                  backgroundColor,
                  color: negativeSum ? 'red' : 'inherit',
                  borderRadius: 10,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                };
              }}
            >
              <Group position="apart">
                <Text weight="bolder">{t('document.sum.total_amount')}</Text>
                <Text weight="bolder" data-testid={TestS.Documents.Edit.SUM}>
                  {FormatS.formatPriceWithCurrency(currency, sum)}
                </Text>
              </Group>
              {taxType === 'gross' && !smallScaleBusiness && taxSummary && (
                <>
                  {taxSummary.taxEntries.map((taxEntry) =>
                    taxEntry.taxRatePercent === 0 ? null : (
                      <Text size="xs" key={taxEntry.taxRatePercent} data-testid={TestS.Documents.Edit.TAX}>
                        {`${t('document.sum.includes_vat')} ${
                          taxEntry.taxRatePercent
                        } % (${FormatS.formatPriceWithCurrency(currency, taxEntry.taxAmount)})`}
                      </Text>
                    ),
                  )}
                </>
              )}
            </Box>
          </Stack>
        ) : (
          <Center>{t('document.sum.hidden')}</Center>
        )}
        {documentType === 'OFFER' && (
          <Checkbox
            checked={summable}
            label={t('document.sum.show_total')}
            onChange={(event) => savePatch({ summable: event.target.checked })}
          />
        )}
      </Stack>
    </Group>
  );
};

export default DocumentSum;
